import TextFieldWithLabel from "@/components/form/hook-form/textfield-with-label";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { isClerkAPIResponseError } from "@clerk/clerk-react/errors";
import { useUser } from "@clerk/clerk-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { memo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { SettingModalHeader } from "../../components/setting-modal-header";

interface ChangePasswordPageProps {
  onBack?: () => void;
  isModal?: boolean;
}

const PasswordSchema = z
  .object({
    currentPassword: z
      .string()
      .min(1, "Current password is required")
      .min(8, "Password must be at least 8 characters"),
    newPassword: z
      .string()
      .min(1, "New password is required")
      .min(8, "Password must be at least 8 characters")
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        "Must contain uppercase, lowercase, and a number",
      ),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: "New password must be different from current password",
    path: ["newPassword"],
  });

type PasswordFormValues = z.infer<typeof PasswordSchema>;

const ChangePasswordPage = memo(function ChangePasswordPage({
  onBack,
  isModal,
}: ChangePasswordPageProps) {
  const { user } = useUser();
  const { showSuccess, showError } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PasswordFormValues>({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    resolver: zodResolver(PasswordSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const onSubmit = async (data: PasswordFormValues) => {
    if (!user) {
      setApiError("User not found. Please log in again.");
      showError("User not found. Please log in again.");
      return;
    }

    setApiError(null);
    setIsSubmitting(true);
    try {
      await user.updatePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
        signOutOfOtherSessions: false,
      });

      showSuccess("Password changed successfully");
      reset();
      onBack?.();
    } catch (error) {
      console.error("Error changing password:", error);
      let message = "Failed to change password. Please try again.";
      if (isClerkAPIResponseError(error)) {
        message = error.errors[0]?.longMessage || error.errors[0]?.message || message;
      } else if (error instanceof Error) {
        message = error.message || message;
      }
      setApiError(message);
      showError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="no-scrollbar min-h-full w-full overflow-y-auto bg-[#fcfbfb]">
      <SettingModalHeader title="Change Password" onBack={onBack} isModal={isModal} />

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-[14px] px-9 pt-4 pb-9"
      >
        <TextFieldWithLabel
          control={control}
          name="currentPassword"
          label="Current Password"
          placeholder="Enter your current password"
          type="password"
          autocomplete="current-password"
          ariaLabel="Current Password"
          id="currentPassword"
          error={errors.currentPassword?.message}
          theme="settings"
          showErrorMessage
        />

        <TextFieldWithLabel
          control={control}
          name="newPassword"
          label="New Password"
          placeholder="Enter your new password"
          type="password"
          autocomplete="new-password"
          ariaLabel="New Password"
          id="newPassword"
          error={errors.newPassword?.message}
          theme="settings"
          showErrorMessage
        />

        <TextFieldWithLabel
          control={control}
          name="confirmPassword"
          label="Confirm New Password"
          placeholder="Confirm your new password"
          type="password"
          autocomplete="new-password"
          ariaLabel="Confirm New Password"
          id="confirmPassword"
          error={errors.confirmPassword?.message}
          theme="settings"
          showErrorMessage
        />

        {apiError && (
          <p className={cn(textVariants.bodySmall, "text-center text-red-600")}>{apiError}</p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="flex h-[52px] w-full items-center justify-center rounded-[27px] bg-[#620ad5] px-[18px] shadow-[0px_8px_64.7px_0px_rgba(64,0,255,0.1)] active:brightness-75 disabled:opacity-60"
        >
          <span className={cn(textVariants.inputText, "text-white")}>
            {isSubmitting ? "Saving…" : "Save New Password"}
          </span>
        </button>
      </form>
    </div>
  );
});

export default ChangePasswordPage;
